import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { fetchAdminProfile, updateAdminProfile } from '@/lib/api';

const ADMIN_TOKEN_KEY = 'medicaclic_admin_token';
const ADMIN_USER_KEY = 'medicaclic_admin_user';

type ProfileForm = {
  name: string;
  email: string;
  currentPassword: string;
  password: string;
  passwordConfirmation: string;
};

const initialForm: ProfileForm = {
  name: '',
  email: '',
  currentPassword: '',
  password: '',
  passwordConfirmation: '',
};

export default function AdminProfilePage() {
  const navigate = useNavigate();
  const [form, setForm] = useState<ProfileForm>(initialForm);
  const [loadingProfile, setLoadingProfile] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  useEffect(() => {
    if (!localStorage.getItem(ADMIN_TOKEN_KEY)) {
      navigate('/admin/login');
      return;
    }

    void fetchAdminProfile()
      .then(({ user }) => {
        setForm((current) => ({ ...current, name: user.name, email: user.email }));
        localStorage.setItem(ADMIN_USER_KEY, JSON.stringify(user));
      })
      .catch((err: unknown) => {
        setError(err instanceof Error ? err.message : 'Impossible de charger le profil.');
      })
      .finally(() => setLoadingProfile(false));
  }, [navigate]);

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setError('');
    setSuccess('');
    setSaving(true);

    try {
      const payload = {
        name: form.name,
        email: form.email,
        ...(form.password ? {
          current_password: form.currentPassword,
          password: form.password,
          password_confirmation: form.passwordConfirmation,
        } : {}),
      };
      const response = await updateAdminProfile(payload);
      localStorage.setItem(ADMIN_USER_KEY, JSON.stringify(response.user));
      setForm((current) => ({
        ...current,
        name: response.user.name,
        email: response.user.email,
        currentPassword: '',
        password: '',
        passwordConfirmation: '',
      }));
      setSuccess(response.message);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Impossible de mettre à jour le profil.');
    } finally {
      setSaving(false);
    }
  };

  const updateField = (field: keyof ProfileForm, value: string) => {
    setForm((current) => ({ ...current, [field]: value }));
  };

  return (
    <div className="min-h-screen bg-slate-100 p-3 text-slate-800 md:p-6">
      <div className="mx-auto max-w-5xl">
        <header className="mb-6 flex flex-col gap-4 rounded-[28px] bg-gradient-to-br from-slate-950 via-slate-900 to-slate-800 p-5 text-white shadow-[0_20px_60px_rgba(15,23,42,0.14)] md:flex-row md:items-center md:justify-between md:p-7">
          <div>
            <p className="text-[10px] font-semibold uppercase tracking-[0.28em] text-primary-200">Compte administrateur</p>
            <h1 className="mt-2 text-2xl font-bold tracking-tight md:text-3xl">Mon profil</h1>
            <p className="mt-1 text-sm text-slate-300">Gérez vos informations et votre mot de passe.</p>
          </div>
          <Link to="/admin" className="w-fit rounded-xl border border-white/10 bg-white/5 px-4 py-2.5 text-sm font-medium text-white transition hover:bg-white/10">
            Retour au dashboard
          </Link>
        </header>

        <main className="grid gap-6 lg:grid-cols-[minmax(0,1fr)_300px]">
          <form onSubmit={handleSubmit} className="rounded-[28px] border border-slate-200 bg-white p-5 shadow-sm md:p-7">
            <div className="border-b border-slate-100 pb-5">
              <p className="text-[11px] font-semibold uppercase tracking-[0.2em] text-slate-500">Informations personnelles</p>
              <h2 className="mt-1 text-xl font-semibold text-slate-900">Informations du compte</h2>
            </div>

            {loadingProfile ? (
              <p className="py-8 text-sm text-slate-500">Chargement du profil...</p>
            ) : (
              <>
                <div className="grid gap-5 py-6 sm:grid-cols-2">
                  <div>
                    <label htmlFor="profile-name" className="mb-1.5 block text-sm font-medium text-slate-700">Nom complet</label>
                    <input id="profile-name" value={form.name} onChange={(event) => updateField('name', event.target.value)} autoComplete="name" required maxLength={255} className="w-full rounded-xl border border-slate-300 px-3 py-2.5 outline-none transition focus:border-primary-500 focus:ring-2 focus:ring-primary-100" />
                  </div>
                  <div>
                    <label htmlFor="profile-email" className="mb-1.5 block text-sm font-medium text-slate-700">Adresse e-mail</label>
                    <input id="profile-email" type="email" value={form.email} onChange={(event) => updateField('email', event.target.value)} autoComplete="email" required maxLength={255} className="w-full rounded-xl border border-slate-300 px-3 py-2.5 outline-none transition focus:border-primary-500 focus:ring-2 focus:ring-primary-100" />
                  </div>
                </div>

                <div className="border-t border-slate-100 pt-6">
                  <p className="text-sm font-semibold text-slate-900">Changer le mot de passe</p>
                  <p className="mt-1 text-sm text-slate-500">Laissez ces champs vides pour conserver votre mot de passe actuel.</p>
                  <div className="mt-4 grid gap-5 sm:grid-cols-2">
                    <div className="sm:col-span-2">
                      <label htmlFor="current-password" className="mb-1.5 block text-sm font-medium text-slate-700">Mot de passe actuel</label>
                      <input id="current-password" type="password" value={form.currentPassword} onChange={(event) => updateField('currentPassword', event.target.value)} autoComplete="current-password" minLength={8} className="w-full rounded-xl border border-slate-300 px-3 py-2.5 outline-none transition focus:border-primary-500 focus:ring-2 focus:ring-primary-100" />
                    </div>
                    <div>
                      <label htmlFor="new-password" className="mb-1.5 block text-sm font-medium text-slate-700">Nouveau mot de passe</label>
                      <input id="new-password" type="password" value={form.password} onChange={(event) => updateField('password', event.target.value)} autoComplete="new-password" minLength={8} className="w-full rounded-xl border border-slate-300 px-3 py-2.5 outline-none transition focus:border-primary-500 focus:ring-2 focus:ring-primary-100" />
                    </div>
                    <div>
                      <label htmlFor="password-confirmation" className="mb-1.5 block text-sm font-medium text-slate-700">Confirmer le nouveau mot de passe</label>
                      <input id="password-confirmation" type="password" value={form.passwordConfirmation} onChange={(event) => updateField('passwordConfirmation', event.target.value)} autoComplete="new-password" minLength={8} className="w-full rounded-xl border border-slate-300 px-3 py-2.5 outline-none transition focus:border-primary-500 focus:ring-2 focus:ring-primary-100" />
                    </div>
                  </div>
                </div>

                {error && <div role="alert" className="mt-5 rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">{error}</div>}
                {success && <div role="status" className="mt-5 rounded-xl border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-700">{success}</div>}

                <div className="mt-6 flex justify-end border-t border-slate-100 pt-5">
                  <button type="submit" disabled={saving} className="w-full rounded-xl bg-primary-600 px-5 py-3 text-sm font-semibold text-white transition hover:bg-primary-700 disabled:cursor-not-allowed disabled:opacity-60 sm:w-auto">
                    {saving ? 'Enregistrement...' : 'Enregistrer les modifications'}
                  </button>
                </div>
              </>
            )}
          </form>

          <aside className="h-fit rounded-[28px] border border-slate-200 bg-white p-5 shadow-sm md:p-6">
            <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-primary-50 text-lg font-bold text-primary-700">M</div>
            <h2 className="mt-4 text-lg font-semibold text-slate-900">Sécurité du compte</h2>
            <p className="mt-2 text-sm leading-6 text-slate-600">Vos modifications sont enregistrées sur votre compte administrateur. Le mot de passe actuel est requis pour en choisir un nouveau.</p>
          </aside>
        </main>
      </div>
    </div>
  );
}